import shaka from "shaka-player";

let player;
let videoElement;
let availableCaptions = [];

export const state = {
  playingState: false
};

export const init = async (element) => {
  shaka.polyfill.installAll(); // polyfilling for devices that need it.

  videoElement = element;

  if (!videoElement) {
    videoElement = document.createElement("video");

    videoElement.style.cssText =
      "position: absolute; top: 0; left: 0; width: 100vw; height: 100vh; object-fit: contain; background: #000;";

    videoElement.width = window.innerWidth;
    videoElement.height = window.innerHeight;

    player = new shaka.Player();
    await player.attach(videoElement);

    videoElement.autoplay = true;
    videoElement.preload = true;
    videoElement.muted = false;

    player.addEventListener("error", (err) => {
      console.error(err);
    });
    
    videoElement.addEventListener("play", () => {
      state.playingState = true;
    });
    
    videoElement.addEventListener("pause", () => {
      state.playingState = false;
    });

    document.body.insertBefore(videoElement, document.body.firstChild);
  }
};
/**
 * Loads the player.
 * @param {Object} config - The player configuration.
 * @returns {Promise<void>}
 */
export const load = async (config) => {
  if (!player || !videoElement) {
    throw "Player not initialized yet";
  }

  await player.load(config.streamUrl);

  player.setTextTrackVisibility(false);

  availableCaptions = [];
  if (config.tracks && Array.isArray(config.tracks)) {
    for (const track of config.tracks) {
      if ((track.kind === "captions" || track.kind === "subtitles") && track.file) {
        availableCaptions.push({
           id: track.file,
           file: track.file,
           label: track.label || "English"
        });
      }
    }
  }
};

export const play = () => {
  videoElement.play().then(() => {
    state.playingState = true;
  });
};

export const pause = () => {
  videoElement.pause();
  state.playingState = false;
};

export const destroy = async () => {
  await player.destroy();

  player = null;
  videoElement.remove();
  videoElement = null;
};

export const getCurrentTime = () => {
  return videoElement.currentTime;
};

export const getVideoDuration = () => {
  return videoElement.duration;
};

export const seek = (time) => {
  if (videoElement) {
    videoElement.currentTime = time;
  }
};

export const setMuted = (muted) => {
  if (videoElement) {
    videoElement.muted = muted;
  }
};

export const getTimeFormat = () => {
  let secondsToMmSs = (seconds) => new Date(seconds * 1000).toISOString().substr(14, 5);
  return `${secondsToMmSs(videoElement.currentTime)} : ${secondsToMmSs(Math.floor(videoElement.duration))}`;
};

export const getQualityOptions = () => {
  if (!player) return [];
  const tracks = player.getVariantTracks();
  const currentTrack = player.getVariantTracks().find(t => t.active);
  const isAuto = player.getConfiguration().abr.enabled;

  const options = [{ id: "auto", label: "Auto", active: isAuto }];

  const heights = new Map();
  tracks.forEach((track) => {
    if (track.type === 'variant' && track.height) {
      if (!heights.has(track.height) || track.videoBandwidth > heights.get(track.height).videoBandwidth) {
        heights.set(track.height, track);
      }
    }
  });

  const sortedTracks = [];
  heights.forEach((track) => sortedTracks.push(track));
  sortedTracks.sort((a, b) => b.height - a.height);

  sortedTracks.forEach((track) => {
    options.push({
      id: track.id.toString(),
      label: `${track.height}p`,
      active: !isAuto && currentTrack && currentTrack.height === track.height
    });
  });

  return options;
};

export const setQuality = (id) => {
  if (!player) return;
  if (id === "auto") {
    player.configure({ abr: { enabled: true } });
  } else {
    player.configure({ abr: { enabled: false } });
    const track = player.getVariantTracks().find((t) => t.id.toString() === id);
    if (track) {
      player.selectVariantTrack(track, true);
    }
  }
};

export const getCaptionOptions = () => {
  if (!player) return [];
  const tracks = player.getTextTracks();
  const isVisible = player.isTextTrackVisible();
  const options = [{ id: "off", label: "Off", active: !isVisible }];

  tracks.forEach((track) => {
    let rawLabel = track.label;
    if (!rawLabel || rawLabel === "TEXTSTREAM" || rawLabel.toLowerCase() === "unknown") {
      rawLabel = (!track.language || track.language === 'en') ? 'English' : track.language.toUpperCase();
    }
    options.push({
      id: track.id.toString(),
      label: rawLabel,
      active: isVisible && track.active
    });
  });

  availableCaptions.forEach((cap) => {
    if (!tracks.some(t => t.originalVideoId === cap.id)) {
      options.push({
        id: cap.id,
        label: cap.label,
        active: false
      });
    }
  });

  return options;
};

export const setCaption = async (id) => {
  if (!player) return;
  if (id === "off") {
    player.setTextTrackVisibility(false);
  } else {
    const track = player.getTextTracks().find((t) => t.id.toString() === id || t.originalVideoId === id);
    if (track) {
      player.selectTextTrack(track);
      player.setTextTrackVisibility(true);
    } else {
      const extTrack = availableCaptions.find(c => c.id === id);
      if (extTrack) {
        const ext = extTrack.file.split('.').pop()?.toLowerCase();
        let mimeType = ext === 'srt' ? 'text/srt' : 'text/vtt';
        try {
          const newTrack = await player.addTextTrackAsync(extTrack.file, "en", "subtitle", mimeType, null, extTrack.label, false);
          player.selectTextTrack(newTrack);
          player.setTextTrackVisibility(true);
        } catch (e) {
          console.warn("[Player] Failed to load external text track", e);
        }
      }
    }
  }
};

export const isVideoPlaying = () => {
  return videoElement ? !videoElement.paused : false;
};

export default {
  init,
  load,
  play,
  pause,
  seek,
  setMuted,
  getCurrentTime,
  getVideoDuration,
  getTimeFormat,
  state,
  destroy,
  getQualityOptions,
  setQuality,
  getCaptionOptions,
  setCaption,
  isVideoPlaying
};
